import React from 'react';
import { twMerge } from 'tailwind-merge';

import type { Breakpoint } from '#/ui/types';
import { calcSpacing, getResponsiveValue, isNil, styleVars } from '#/ui/utils/general';

export type GridColumns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface GridProps {
  /**
   * The number of columns to use.
   * @default 12
   */
  columns?: GridColumns;
  /**
   * The size of the grid.
   * @default 0
   */
  size?: GridColumns | Partial<Record<Breakpoint, GridColumns>>;
  /**
   * The default spacing across the grid.
   * @default 0
   */
  spacing?: number | Partial<Record<Breakpoint, number>>;
  /**
   * The spacing for the rows (Y-axis).
   * @default false
   */
  rowSpacing?: number | Partial<Record<Breakpoint, number>>;
  /**
   * The spacing for the cols (X-axis).
   * @default false
   */
  colSpacing?: number | Partial<Record<Breakpoint, number>>;
  /**
   * If the grid should be a container.
   * @default false
   */
  container?: boolean;
  /**
   * The className to apply.
   */
  className?: string;
  /**
   * The children of the grid.
   */
  children?: React.ReactNode;
}

/**
 * Grid component, that mirrors the MUI Grid component, using Tailwind CSS.
 */
function Grid({
  spacing = 0,
  columns = 12,
  rowSpacing,
  colSpacing,
  size = 12,
  container = false,
  className,
  children,
}: GridProps) {
  const responsiveSpacing = React.useMemo(() => {
    return {
      base: typeof spacing === 'number' ? spacing : getResponsiveValue(spacing, 'xs'),
      xs: getResponsiveValue(spacing, 'xs'),
      sm: getResponsiveValue(spacing, 'sm'),
      md: getResponsiveValue(spacing, 'md'),
      lg: getResponsiveValue(spacing, 'lg'),
      xl: getResponsiveValue(spacing, 'xl'),
      '2xl': getResponsiveValue(spacing, '2xl'),
    };
  }, [spacing]);

  const spacingClassnames = React.useMemo(() => {
    if (!container) {
      return;
    }

    if (typeof spacing === 'number') {
      return 'gap-(--gooey-grid-spacing)';
    }

    return [
      !isNil(responsiveSpacing['2xl']) && '2xl:gap-(--gooey-grid-spacing-2xl)',
      !isNil(responsiveSpacing.xl) && 'xl:gap-(--gooey-grid-spacing-xl)',
      !isNil(responsiveSpacing.lg) && 'lg:gap-(--gooey-grid-spacing-lg)',
      !isNil(responsiveSpacing.md) && 'md:gap-(--gooey-grid-spacing-md)',
      !isNil(responsiveSpacing.sm) && 'sm:gap-(--gooey-grid-spacing-sm)',
      !isNil(responsiveSpacing.xs) && 'gap-(--gooey-grid-spacing)',
    ];
  }, [responsiveSpacing, spacing, container]);

  const responsiveColSpacing = React.useMemo(() => {
    return {
      base:
        typeof colSpacing === 'number' ? colSpacing : getResponsiveValue(colSpacing ?? null, 'xs'),
      xs: getResponsiveValue(colSpacing ?? null, 'xs'),
      sm: getResponsiveValue(colSpacing ?? null, 'sm'),
      md: getResponsiveValue(colSpacing ?? null, 'md'),
      lg: getResponsiveValue(colSpacing ?? null, 'lg'),
      xl: getResponsiveValue(colSpacing ?? null, 'xl'),
      '2xl': getResponsiveValue(colSpacing ?? null, '2xl'),
    };
  }, [colSpacing]);

  const colSpacingClassnames = React.useMemo(() => {
    if (!container) {
      return;
    }

    if (typeof colSpacing === 'number') {
      return 'gap-x-(--gooey-grid-col-spacing)';
    }

    return [
      !isNil(responsiveColSpacing['2xl']) && '2xl:gap-x-(--gooey-grid-col-spacing-2xl)',
      !isNil(responsiveColSpacing.xl) && 'xl:gap-x-(--gooey-grid-col-spacing-xl)',
      !isNil(responsiveColSpacing.lg) && 'lg:gap-x-(--gooey-grid-col-spacing-lg)',
      !isNil(responsiveColSpacing.md) && 'md:gap-x-(--gooey-grid-col-spacing-md)',
      !isNil(responsiveColSpacing.sm) && 'sm:gap-x-(--gooey-grid-col-spacing-sm)',
      !isNil(responsiveColSpacing.xs) && 'gap-x-(--gooey-grid-col-spacing)',
    ];
  }, [responsiveColSpacing, colSpacing, container]);

  const responsiveRowSpacing = React.useMemo(() => {
    const base =
      typeof rowSpacing === 'number' ? rowSpacing : getResponsiveValue(rowSpacing ?? null, 'xs');
    return {
      base: base ?? responsiveColSpacing.base,
      xs: getResponsiveValue(rowSpacing ?? null, 'xs'),
      sm: getResponsiveValue(rowSpacing ?? null, 'sm'),
      md: getResponsiveValue(rowSpacing ?? null, 'md'),
      lg: getResponsiveValue(rowSpacing ?? null, 'lg'),
      xl: getResponsiveValue(rowSpacing ?? null, 'xl'),
      '2xl': getResponsiveValue(rowSpacing ?? null, '2xl'),
    };
  }, [rowSpacing, responsiveColSpacing]);

  const rowSpacingClassnames = React.useMemo(() => {
    if (!container) {
      return;
    }

    if (typeof rowSpacing === 'number') {
      return 'gap-y-(--gooey-grid-row-spacing)';
    }

    return [
      !isNil(responsiveRowSpacing['2xl']) && '2xl:gap-y-(--gooey-grid-row-spacing-2xl)',
      !isNil(responsiveRowSpacing.xl) && 'xl:gap-y-(--gooey-grid-row-spacing-xl)',
      !isNil(responsiveRowSpacing.lg) && 'lg:gap-y-(--gooey-grid-row-spacing-lg)',
      !isNil(responsiveRowSpacing.md) && 'md:gap-y-(--gooey-grid-row-spacing-md)',
      !isNil(responsiveRowSpacing.sm) && 'sm:gap-y-(--gooey-grid-row-spacing-sm)',
      !isNil(responsiveRowSpacing.xs) && 'gap-y-(--gooey-grid-row-spacing)',
    ];
  }, [rowSpacing, responsiveRowSpacing, container]);

  const responsiveSize = React.useMemo(() => {
    if (container) {
      return {
        base: null,
        xs: null,
        sm: null,
        md: null,
        lg: null,
        xl: null,
      };
    }

    return {
      base: typeof size === 'number' ? size : getResponsiveValue(size, 'xs'),
      xs: getResponsiveValue(size, 'xs'),
      sm: getResponsiveValue(size, 'sm'),
      md: getResponsiveValue(size, 'md'),
      lg: getResponsiveValue(size, 'lg'),
      xl: getResponsiveValue(size, 'xl'),
      '2xl': getResponsiveValue(size, '2xl'),
    };
  }, [size, container]);

  const sizeClassnames = React.useMemo(() => {
    if (container) {
      return;
    }

    if (typeof size === 'number') {
      return 'col-span-(--gooey-grid-size)';
    }

    return [
      !isNil(responsiveSize['2xl']) && '2xl:col-span-(--gooey-grid-size-2xl)',
      !isNil(responsiveSize.xl) && 'xl:col-span-(--gooey-grid-size-xl)',
      !isNil(responsiveSize.lg) && 'lg:col-span-(--gooey-grid-size-lg)',
      !isNil(responsiveSize.md) && 'md:col-span-(--gooey-grid-size-md)',
      !isNil(responsiveSize.sm) && 'sm:col-span-(--gooey-grid-size-sm)',
      !isNil(responsiveSize.xs) && 'col-span-(--gooey-grid-size)',
    ];
  }, [responsiveSize, size, container]);

  const columnsClassnames = React.useMemo(() => {
    if (!container) {
      return;
    }

    return colMap[columns];
  }, [columns, container]);

  const style = React.useMemo(() => {
    return {
      ...styleVars({
        '--gooey-grid-columns': `repeat(${columns}, minmax(0, 1fr))`,
      }),
      ...styleVars(
        {
          '--gooey-grid-spacing': responsiveSpacing.base ?? responsiveSpacing.xs,
          '--gooey-grid-spacing-sm': responsiveSpacing.sm,
          '--gooey-grid-spacing-md': responsiveSpacing.md,
          '--gooey-grid-spacing-lg': responsiveSpacing.lg,
          '--gooey-grid-spacing-xl': responsiveSpacing.xl,
        },
        (value) => calcSpacing(Number(value))
      ),
      ...styleVars(
        {
          '--gooey-grid-col-spacing': responsiveColSpacing.base ?? responsiveColSpacing.xs,
          '--gooey-grid-col-spacing-sm': responsiveColSpacing.sm,
          '--gooey-grid-col-spacing-md': responsiveColSpacing.md,
          '--gooey-grid-col-spacing-lg': responsiveColSpacing.lg,
          '--gooey-grid-col-spacing-xl': responsiveColSpacing.xl,
        },
        (value) => calcSpacing(Number(value))
      ),
      ...styleVars(
        {
          '--gooey-grid-row-spacing': responsiveRowSpacing.base ?? responsiveRowSpacing.xs,
          '--gooey-grid-row-spacing-sm': responsiveRowSpacing.sm,
          '--gooey-grid-row-spacing-md': responsiveRowSpacing.md,
          '--gooey-grid-row-spacing-lg': responsiveRowSpacing.lg,
          '--gooey-grid-row-spacing-xl': responsiveRowSpacing.xl,
        },
        (value) => calcSpacing(Number(value))
      ),
      ...styleVars({
        '--gooey-grid-size': responsiveSize.base ?? responsiveSize.xs,
        '--gooey-grid-size-sm': responsiveSize.sm,
        '--gooey-grid-size-md': responsiveSize.md,
        '--gooey-grid-size-lg': responsiveSize.lg,
        '--gooey-grid-size-xl': responsiveSize.xl,
      }),
    };
  }, [responsiveSpacing, responsiveColSpacing, responsiveRowSpacing, responsiveSize, columns]);

  return (
    <div
      className={twMerge(
        'gooey-grid',
        container && 'gooey-grid-container grid w-full',
        columnsClassnames,
        spacingClassnames,
        rowSpacingClassnames,
        colSpacingClassnames,
        sizeClassnames,
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
}
Grid.displayName = 'GooeyGrid';

export default Grid;

/*
|------------------
| Internals
|------------------
*/

const colMap = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  7: 'grid-cols-7',
  8: 'grid-cols-8',
  9: 'grid-cols-9',
  10: 'grid-cols-10',
  11: 'grid-cols-11',
  12: 'grid-cols-12',
} as const;
